import React, { useState } from 'react';
import { calculateSolarPotential } from '../services/geminiService';

export const SolarCalculator: React.FC = () => {
  const [formData, setFormData] = useState({
    location: '',
    roofArea: '',
    consumption: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    const response = await calculateSolarPotential(
      formData.location,
      Number(formData.roofArea),
      Number(formData.consumption)
    );

    setResult(response);
    setLoading(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="rechner" className="py-16 bg-gradient-to-br from-solar-400 to-solar-600 text-white rounded-3xl shadow-2xl mx-4 lg:mx-0 my-12 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-60 h-60 bg-yellow-300 opacity-20 rounded-full blur-2xl"></div>

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 drop-shadow-md">
            Jetzt Sparpotenzial berechnen
          </h2>
          <p className="text-xl opacity-90">
            Nutzen Sie unsere KI-Analyse um herauszufinden, wie viel Geld Sie mit einer günstigen Photovoltaikanlage sparen können.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-10 border border-white/20">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col">
              <label htmlFor="location" className="mb-2 font-medium text-white/90">Postleitzahl / Ort</label>
              <input
                type="text"
                id="location"
                name="location"
                required
                placeholder="z.B. 10437 Berlin"
                className="px-4 py-3 rounded-xl bg-white text-gray-900 border-0 focus:ring-4 focus:ring-yellow-300 transition-all outline-none"
                value={formData.location}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="roofArea" className="mb-2 font-medium text-white/90">Dachfläche (ca. m²)</label>
              <input
                type="number"
                id="roofArea"
                name="roofArea"
                required
                placeholder="z.B. 45"
                className="px-4 py-3 rounded-xl bg-white text-gray-900 border-0 focus:ring-4 focus:ring-yellow-300 transition-all outline-none"
                value={formData.roofArea}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="consumption" className="mb-2 font-medium text-white/90">Stromverbrauch (kWh/Jahr)</label>
              <input
                type="number"
                id="consumption"
                name="consumption"
                required
                placeholder="z.B. 3500"
                className="px-4 py-3 rounded-xl bg-white text-gray-900 border-0 focus:ring-4 focus:ring-yellow-300 transition-all outline-none"
                value={formData.consumption}
                onChange={handleChange}
              />
            </div>
            
            <div className="md:col-span-3 mt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-eco-700 hover:bg-eco-900 text-white text-xl font-bold py-4 px-8 rounded-xl shadow-lg transform transition-all hover:-translate-y-1 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analysiere Daten...
                  </span>
                ) : (
                  "Kostenlos berechnen & Angebot anfordern"
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8 bg-white text-gray-800 rounded-xl p-6 shadow-inner animate-fade-in border-l-4 border-eco-500">
              <h3 className="text-xl font-bold text-eco-700 mb-2">Ihr persönliches Photovoltaik-Ergebnis:</h3>
              <div 
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: result }}
              />
              <p className="mt-4 text-xs text-gray-500 italic">
                *Hinweis: Dies ist eine KI-basierte Schätzung. Für ein verbindliches Angebot kontaktieren Sie uns bitte direkt.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};